export const RELOAD_COOLDOWN_MS = 60_000;

export const RELEASE_HEADER = "X-Release";

const MARKER_KEY = "staleBuildReloadAt";

const SERVER_RELEASE_KEY = "serverRelease";

const RELOADING_ACTIONS = new Set(["PUSH", "POP"]);

export const shouldReload = ({ lastReloadAt, now }) =>
  lastReloadAt === null || now - lastReloadAt >= RELOAD_COOLDOWN_MS;

export const isReloadPending = ({ lastReloadAt, pageStartedAt }) =>
  lastReloadAt !== null && lastReloadAt >= pageStartedAt;

export const isReleaseMismatch = ({ clientRelease, serverRelease }) =>
  Boolean(clientRelease) &&
  Boolean(serverRelease) &&
  clientRelease !== serverRelease;

export const shouldReloadOnNavigation = ({ action, ...releases }) =>
  RELOADING_ACTIONS.has(action) && isReleaseMismatch(releases);

const parseMarker = (raw) => {
  const value = Number(raw);
  return raw !== null && Number.isFinite(value) ? value : null;
};

// sessionStorage access throws in some private modes and sandboxed iframes.
const readItem = (win, key) => {
  try {
    return { readable: true, value: win.sessionStorage.getItem(key) };
  } catch {
    return { readable: false, value: null };
  }
};

const writeItem = (win, key, value) => {
  try {
    win.sessionStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
};

const readMarker = (win) => {
  const { readable, value } = readItem(win, MARKER_KEY);
  return { readable, lastReloadAt: parseMarker(value) };
};

const reloadUnlessCoolingDown = (win) => {
  const now = Date.now();
  const { readable, lastReloadAt } = readMarker(win);
  const reloads =
    readable &&
    shouldReload({ lastReloadAt, now }) &&
    writeItem(win, MARKER_KEY, String(now));
  return reloads ? win.location.reload() : undefined;
};

export const isReloadingForStaleBuild = (win) =>
  isReloadPending({
    lastReloadAt: readMarker(win).lastReloadAt,
    pageStartedAt: win.performance.timeOrigin,
  });

/* Vite dispatches vite:preloadError when a dynamic import fails. Cancelling it makes the
 * import resolve undefined, so the event is left alone and the import still rejects. */
export const installStaleChunkReload = (win) =>
  win.addEventListener("vite:preloadError", () => reloadUnlessCoolingDown(win));

export const recordServerRelease = (win, serverRelease) =>
  serverRelease ? writeItem(win, SERVER_RELEASE_KEY, serverRelease) : false;

export const reloadOnStaleRelease =
  (win, clientRelease) => (_location, action) =>
    shouldReloadOnNavigation({
      action,
      clientRelease,
      serverRelease: readItem(win, SERVER_RELEASE_KEY).value,
    })
      ? reloadUnlessCoolingDown(win)
      : undefined;
